import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, MailCheck, MailX } from "lucide-react";
import { AuthLayout } from "@/components/layout/AuthLayout";
import { api, apiError } from "@/lib/api";

const Unavailable = () => (
  <div className="surface mt-10 p-6" role="status" data-testid="forgot-unavailable">
    <MailX className="h-7 w-7 text-primary" aria-hidden />
    <p className="mt-4">Email recovery is currently unavailable. Please contact the club team for help.</p>
  </div>
);

export default function ForgotPassword() {
  const [available, setAvailable] = useState(null);
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [sent, setSent] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/auth/password-reset/availability").then(({ data }) => setAvailable(data.available)).catch(() => setAvailable(false));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(email)) return setError("Enter a valid email address");
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setSent(data.message);
    } catch (err) {
      if (err?.response?.status === 503) setAvailable(false);
      else setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout testId="forgot-page" headline="Back to your circle.">
      <h1 className="font-display text-5xl leading-[0.95] sm:text-6xl">Reset password</h1>
      {available !== null && (
        <p className="mt-4 text-muted-foreground" data-testid="forgot-subtitle">
          {available ? "Enter your account email and we'll send you a reset link." : "Get help accessing your account."}
        </p>
      )}
      {available === null && <div className="mt-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" aria-label="Loading" /></div>}
      {available === false && <Unavailable />}
      {available && sent && (
        <div className="surface mt-10 p-6" role="status" data-testid="forgot-sent">
          <MailCheck className="h-7 w-7 text-primary" aria-hidden />
          <p className="mt-4">{sent}</p>
        </div>
      )}
      {available && !sent && (
        <form onSubmit={submit} noValidate className="mt-10 space-y-6">
          <div>
            <label htmlFor="fp-email" className="field-label">Email address</label>
            <input id="fp-email" type="email" autoComplete="email" className="field" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email-input" />
          </div>
          {error && <p role="alert" className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm" data-testid="forgot-error">{error}</p>}
          <button type="submit" className="btn-primary min-h-[56px] w-full text-lg" disabled={busy} data-testid="forgot-submit-button">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Send reset link"}
          </button>
        </form>
      )}
      <p className="mt-8 text-center text-sm text-muted-foreground"><Link to="/signin" className="font-semibold text-primary underline underline-offset-4" data-testid="forgot-back-signin">Back to sign in</Link></p>
    </AuthLayout>
  );
}
